import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { clearPortalLocalStorage } from '@rsos/base-utils/clearPortalLocalStorage';
import { PORTAL_SESSION_PRIORITY } from '@rsos/capstone/src/constants/portalPriorities';
import { gspx } from '@rsos/gainsight';
import { logout, sessionEnd } from '@rsos/sinatra';
import { getSessionNotifyAge } from '@rsos/utils/metaTags';
import SessionModal from '../SessionModal';

/**
 * Orchestrates the three-stage session timeout warning sequence:
 *   1. "first"  — SESSION_NOTIFY_AGE seconds before expiry (default 10 min)
 *   2. "second" — 5 minutes before expiry
 *   3. "final"  — at expiry; SessionModal auto-logs out after 3s
 *
 * This is a wall-clock countdown from login — not an inactivity timer.
 * SESSION_AGE (default 48hrs) and SESSION_NOTIFY_AGE (default 10min) are
 * injected as HTML meta tags by portal_renderer env vars.
 */
const SessionPortal = () => {
  const [isActive, setIsActive] = useState(false);
  const [warning, setWarning] = useState(null);
  const dispatch = useDispatch();

  // sessionExpiration is a unix timestamp in seconds: sessionStart + SESSION_AGE.
  // Persisted to localStorage via redux-persist — page reloads do not reset the clock.
  const session = useSelector(state => state.sinatra.auth.session);

  // Tracks which warnings have fired for the current sessionExpiration.
  // Using a ref avoids re-renders on every tick. Resets when sessionExpiration
  // changes so a new login replays the full warning sequence.
  const shownWarningsRef = useRef({
    sessionExpiration: null,
    warnings: new Set(),
  });

  useEffect(() => {
    const { sessionStart, sessionExpiration } = session;

    // Wait for session to be initialized (rehydration from redux-persist is async).
    if (sessionStart == null || sessionExpiration == null) {
      return undefined;
    }

    // Reset warning state for the new session expiration value.
    shownWarningsRef.current = {
      sessionExpiration,
      warnings: new Set(),
    };
    setIsActive(false);
    setWarning(null);

    // Check immediately on mount — handles returning to a tab with an already-expired session.
    checkIsSessionExpiring(sessionExpiration);

    // sessionExpiration is passed as an argument so the interval always uses
    // the value captured when this effect ran, avoiding stale closure issues.
    const interval = setInterval(() => {
      checkIsSessionExpiring(sessionExpiration);
    }, 1000);

    // Tear down interval when sessionExpiration changes or component unmounts.
    return () => clearInterval(interval);
    // checkIsSessionExpiring and session are intentionally omitted: the function
    // is defined in the same scope and session values are passed as arguments to
    // avoid stale closures — the deps array drives re-runs, not the function ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.sessionExpiration, session.sessionStart]);

  /**
   * Determines which warning stage to show based on current time vs expiration.
   *
   * Thresholds:
   *   firstWarning  = sessionExpiration - max(SESSION_NOTIFY_AGE, 300)
   *   secondWarning = sessionExpiration - min(SESSION_NOTIFY_AGE, 300)
   *
   * max/min normalization ensures 'first' always fires before 'second' even if
   * SESSION_NOTIFY_AGE is configured below 300s.
   *
   * >= instead of === ensures warnings fire even if the interval was delayed
   * by browser throttling (backgrounded tab, sleep, etc).
   */
  function checkIsSessionExpiring(sessionExpiration) {
    if (sessionExpiration == null) {
      return;
    }

    // Sync ref if sessionExpiration changed between effect runs.
    if (shownWarningsRef.current.sessionExpiration !== sessionExpiration) {
      shownWarningsRef.current = {
        sessionExpiration,
        warnings: new Set(),
      };
    }

    const sessionNotifyAge = getSessionNotifyAge();
    const firstWarningLead = Math.max(sessionNotifyAge, 300);
    const secondWarningLead = Math.min(sessionNotifyAge, 300);
    const firstWarning = sessionExpiration - firstWarningLead;
    const secondWarning = sessionExpiration - secondWarningLead;
    const dateNowSeconds = Math.floor(Date.now() / 1000);

    if (dateNowSeconds >= sessionExpiration) {
      // Expired — SessionModal will trigger handleLogout after a 3s grace period.
      showWarning('final');
    } else if (dateNowSeconds >= secondWarning) {
      // 5 min remaining — re-opens even if user dismissed the first warning.
      showWarning('second');
    } else if (dateNowSeconds >= firstWarning) {
      // SESSION_NOTIFY_AGE remaining (default 10 min) — first heads-up.
      showWarning('first');
    }
  }

  // Each warning fires exactly once per sessionExpiration value.
  // shownWarningsRef prevents re-opening the modal on every tick after a threshold is crossed.
  function showWarning(nextWarning) {
    if (shownWarningsRef.current.warnings.has(nextWarning)) {
      return;
    }

    shownWarningsRef.current.warnings.add(nextWarning);
    setIsActive(true);
    setWarning(nextWarning);
  }

  // useCallback keeps the reference stable across re-renders so SessionModal's
  // useEffect (which has onClose/onLogout as deps) doesn't cancel and restart
  // the 3s logout timer during the final warning phase.
  const handleClose = useCallback(() => {
    setIsActive(false);
  }, []);

  const handleLogout = useCallback(() => {
    handleClose();
    clearPortalLocalStorage();
    dispatch(sessionEnd());
    gspx.trackCustomEvent('Auth Session', {
      name: 'Session End',
      Category: 'Session tracking',
      'Launched date': new Date(),
    });
    dispatch(logout());
    window.location = '/';
  }, [dispatch, handleClose]);

  return (
    <SessionModal
      isOpened={isActive}
      onLogout={handleLogout}
      onClose={handleClose}
      expiresAt={session.sessionExpiration}
      warning={warning}
      portalPriority={PORTAL_SESSION_PRIORITY}
    />
  );
};

export default SessionPortal;
